import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from './icons';
import { COINS, UNIQUE_COINS, coinKey, type Coin } from './TokenSheet';
import { useWallet } from '../../hooks/useWallet';
import { useTotalBalance } from '../../hooks/useTotalBalance';
import { DISPLAY_DECIMALS, formatUsd } from '../../lib/format';
import { HIDDEN_BALANCE, INCPT_COIN_ID, useUiStore } from '../../store/ui';
import { useIncptAsset } from '../../hooks/useIncptAsset';
import { IncptIcon } from '../icons/IncptIcon';
import { formatDecimalDisplay } from '@incpt/shared';

const fmtIncptUsd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

/** Tile captions set the shared column width. TRX drops two fraction digits
 *  so a balance like 2,135.538905 does not stretch every tile. */
function tileAmount(amount: number, symbol: Coin['symbol']): string {
  const digits = DISPLAY_DECIMALS[symbol] ?? 2;
  const max = symbol === 'TRX' ? digits - 2 : digits;
  if (!Number.isFinite(amount)) return '0';
  return amount.toLocaleString('en-US', { maximumFractionDigits: max });
}

type Props = {
  balance?: string;
  onSelectCoin?: (coin: Coin) => void;
  onIncpt?: () => void;
};

export function DefiWalletCard({ balance, onSelectCoin, onIncpt }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { byTokenChain, byTokenChainUsd } = useWallet();
  // Same cut as the home total: a coin hidden on its own screen drops out.
  const { walletUsd, incptUsd } = useTotalBalance();
  const incpt = useIncptAsset();
  const balanceHidden = useUiStore((s) => s.balanceHidden);
  const coinBalanceHidden = useUiStore((s) => s.coinBalanceHidden);

  const rowHidden = (coin: Coin) =>
    balanceHidden ||
    COINS.filter((c) => c.symbol === coin.symbol).some((c) => !!coinBalanceHidden[coinKey(c)]);
  const incptHidden = balanceHidden || !!coinBalanceHidden[INCPT_COIN_ID];

  const toRow = (coin: Coin) => {
    const chains = COINS.filter((c) => c.symbol === coin.symbol);
    return {
      coin,
      amount: chains.reduce((sum, c) => sum + (byTokenChain[`${c.symbol}:${c.chain}`] ?? 0), 0),
      usd: chains.reduce((sum, c) => sum + (byTokenChainUsd[`${c.symbol}:${c.chain}`] ?? 0), 0),
    };
  };
  // Stables, then natives — with INCPT in TRX's slot and TRX last.
  const stableRows = UNIQUE_COINS.filter((c) => c.symbol === 'USDT' || c.symbol === 'USDC').map(
    toRow,
  );
  const otherRows = UNIQUE_COINS.filter((c) => c.symbol !== 'USDT' && c.symbol !== 'USDC').map(
    toRow,
  );
  const trxAt = otherRows.findIndex((r) => r.coin.symbol === 'TRX');
  const swapIncptWithTrx = incpt.visible && trxAt >= 0;
  const headerBalance = balanceHidden
    ? HIDDEN_BALANCE
    : (balance ?? `$${formatUsd(walletUsd + incptUsd)}`);

  const gridRef = useRef<HTMLDivElement>(null);
  // Labels that change a tile's intrinsic width. The effect remeasures from these.
  const widthKey = [
    ...stableRows,
    ...otherRows,
  ]
    .map(({ coin, amount, usd }) => {
      const hidden = rowHidden(coin);
      return hidden
        ? `${coin.symbol}:h`
        : `${coin.symbol}:${formatUsd(usd)}:${tileAmount(amount, coin.symbol)}`;
    })
    .concat(
      incpt.visible
        ? incptHidden
          ? 'INCPT:h'
          : `INCPT:${fmtIncptUsd(incpt.usd)}:${formatDecimalDisplay(incpt.amount)}`
        : 'INCPT:off',
      open ? 'open' : 'closed',
    )
    .join('|');

  useLayoutEffect(() => {
    const root = gridRef.current;
    if (!root) return;
    let cancelled = false;

    const apply = () => {
      if (cancelled || !root.isConnected) return;
      // Drop the shared width so each column falls back to its own content,
      // then copy the widest column onto every column.
      root.style.gridAutoColumns = '';
      const tiles = [...root.querySelectorAll<HTMLElement>(':scope > button')];
      let max = 0;
      for (const tile of tiles) max = Math.max(max, tile.getBoundingClientRect().width);
      if (max <= 0) return;
      root.style.gridAutoColumns = `${Math.ceil(max)}px`;
    };

    apply();
    void document.fonts?.ready.then(apply);
    window.addEventListener('resize', apply);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', apply);
    };
  }, [widthKey]);

  const coinTile = ({ coin, amount, usd }: ReturnType<typeof toRow>) => {
    const hidden = rowHidden(coin);
    return (
      <TokenTile
        key={coin.symbol}
        icon={<coin.Icon size={38} />}
        symbol={coin.symbol}
        value={hidden ? HIDDEN_BALANCE : `$${formatUsd(usd)}`}
        caption={
          hidden ? '••••' : `${tileAmount(amount, coin.symbol)} ${coin.symbol}`
        }
        onClick={() => onSelectCoin?.(coin)}
      />
    );
  };

  return (
    <div
      className="overflow-hidden rounded-[24px]"
      style={{
        background: 'rgba(255,255,255,0.82)',
        border: '1px solid rgba(255,255,255,0.7)',
        boxShadow: '0px 10px 26px rgba(30,40,90,0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex h-16 w-full items-center justify-between p-4"
        aria-expanded={open}
      >
        <span className="text-[16px] text-ink">{t('wallet.defiWallet')}</span>
        <span className="flex items-center gap-1.5">
          <span className="text-[18px] font-medium tabular-nums text-ink">{headerBalance}</span>
          <ChevronRight
            size={18}
            className={`text-ink transition-transform duration-500 ${open ? '-rotate-90' : 'rotate-90'}`}
          />
        </span>
      </button>

      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          {/* Shared width = the widest tile. auto-cols-max is only the
              pre-measure fallback; the effect overwrites it in px. */}
          <div
            ref={gridRef}
            className="grid snap-x snap-mandatory auto-cols-max grid-flow-col grid-rows-2 gap-2.5 overflow-x-auto border-t border-black/[0.06] px-3.5 pb-4 pt-2.5 [scroll-padding-left:14px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {stableRows.map(coinTile)}
            {(swapIncptWithTrx ? otherRows.slice(0, trxAt) : otherRows).map(coinTile)}
            {incpt.visible && (
              <TokenTile
                icon={<IncptIcon size={38} scale={1.2} />}
                symbol={incpt.symbol}
                value={incptHidden ? HIDDEN_BALANCE : fmtIncptUsd(incpt.usd)}
                caption={
                  incptHidden
                    ? '••••'
                    : `${formatDecimalDisplay(incpt.amount)} ${incpt.symbol}`
                }
                onClick={onIncpt}
              />
            )}
            {swapIncptWithTrx && otherRows.slice(trxAt + 1).map(coinTile)}
            {swapIncptWithTrx && coinTile(otherRows[trxAt]!)}
          </div>
        </div>
      </div>
    </div>
  );
}

function TokenTile({
  icon,
  symbol,
  value,
  caption,
  onClick,
}: {
  icon: ReactNode;
  symbol: string;
  value: string;
  caption: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-full w-full min-w-max snap-start items-center gap-2 rounded-2xl bg-black/[0.025] px-2.5 py-3 text-left shadow-[0_0_0_0.5px_rgba(0,0,0,0.03)] active:opacity-70 min-[400px]:gap-[11px] min-[400px]:px-3.5"
    >
      {/* Box must match the Icon size={38} — a smaller overflow:hidden wrapper
          clipped the bottom of every coin circle on phone widths. */}
      <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center max-[340px]:h-8 max-[340px]:w-8 [&>*]:!h-full [&>*]:!w-full [&_img]:!h-full [&_img]:!w-full">{icon}</span>
      <span>
        <span className="block whitespace-nowrap text-[13px] font-semibold leading-none text-ink">{symbol}</span>
        <span className="mt-1 block whitespace-nowrap text-[13px] leading-[1.2] tracking-[-0.4px] tabular-nums text-ink min-[400px]:text-[15px]">
          {value}
        </span>
        <span className="mt-0.5 block whitespace-nowrap text-[11px] text-muted">{caption}</span>
      </span>
    </button>
  );
}
